import { store } from '@ports/common/store';
import { dotenv } from '@utils/constants/env';
import { io, Socket } from 'socket.io-client';
// import { toast } from "sonner";

export type SocketEventHandler = (data: any) => void;

export interface SocketEventSubscription {
  event: string;
  handler: SocketEventHandler;
  serviceId: string;
}

export const CHAT_SOCKET_URL = dotenv.CHAT_SOCKET_URL;

export class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private connectionCallbacks: Map<string, (connected: boolean) => void> =
    new Map();
  private eventSubscriptions: Map<string, SocketEventSubscription[]> =
    new Map();
  private isConnecting: boolean = false;
  private isConnected: boolean = false;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  public isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  public async connect(): Promise<void> {
    if (this.isConnecting) {
      console.log('Socket connection already in progress, waiting...');
      return this.waitForConnection();
    }

    if (this.isConnected && this.socket?.connected) {
      console.log('Socket already connected with the same email');
      return Promise.resolve();
    }

    this.isConnecting = true;

    try {
      // Disconnect existing socket if any
      if (this.socket) {
        console.log('Disconnecting existing socket before reconnecting');
        this.socket.disconnect();
        this.socket = null;
      }

      this.socket = io(CHAT_SOCKET_URL, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      const storeToken = store?.getState()?.sessionStateSlice?.token;
      const sessionToken = sessionStorage.getItem('token');
      const token = storeToken ? storeToken : sessionToken;
      if (token) {
        this.socket.auth = { token };
      }

      this.setupSocketListeners();

      return new Promise<void>((resolve, reject) => {
        if (!this.socket) {
          this.isConnecting = false;
          reject(new Error('Failed to create socket instance'));
          return;
        }

        const onConnect = () => {
          this.socket?.off('connect', onConnect);
          this.socket?.off('connect_error', onError);
          this.isConnecting = false;
          this.isConnected = true;
          console.log('Socket connected successfully', {
            socketId: this.socket?.id
          });
          this.notifyConnectionCallbacks(true);
          resolve();
        };

        const onError = (error: any) => {
          this.socket?.off('connect', onConnect);
          this.socket?.off('connect_error', onError);
          this.isConnecting = false;
          this.isConnected = false;
          console.error('Socket connection error:', error);
          this.notifyConnectionCallbacks(false);
          reject(error);
        };

        this.socket.once('connect', onConnect);
        this.socket.once('connect_error', onError);

        // Timeout fallback
        setTimeout(() => {
          if (this.isConnecting) {
            this.socket?.off('connect', onConnect);
            this.socket?.off('connect_error', onError);
            this.isConnecting = false;
            console.warn('Socket connection timeout');
            resolve(); // Resolve even on timeout to not block the app
          }
        }, 5000);
      });
    } catch (error) {
      this.isConnecting = false;
      this.isConnected = false;
      console.error('Error during socket connection:', error);
      throw error;
    }
  }

  private async waitForConnection(): Promise<void> {
    return new Promise<void>(resolve => {
      const checkConnection = () => {
        if (!this.isConnecting) {
          resolve();
        } else {
          setTimeout(checkConnection, 100);
        }
      };
      checkConnection();
    });
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnected = true;
      // toast.success("Connected to agent server");
      // store.dispatch(setIsSocketConnected(true));
      this.notifyConnectionCallbacks(true);
    });

    this.socket.on('disconnect', reason => {
      console.log('Socket disconnected', { reason });
      this.isConnected = false;
      // toast.error("Disconnected from agent server");
      // store.dispatch(setIsSocketConnected(false));
      this.notifyConnectionCallbacks(false);
    });

    this.socket.on('connect_error', error => {
      console.error('Socket connection error:', error.message, error);
      this.isConnected = false;
      this.notifyConnectionCallbacks(false);
    });

    // Setup existing event subscriptions
    this.eventSubscriptions.forEach((subscriptions, event) => {
      subscriptions.forEach(subscription => {
        this.socket?.on(event, subscription.handler);
      });
    });
  }

  public subscribe(
    serviceId: string,
    event: string,
    handler: SocketEventHandler
  ): void {
    if (!this.eventSubscriptions.has(event)) {
      this.eventSubscriptions.set(event, []);
    }

    const subscriptions = this.eventSubscriptions.get(event)!;

    // Remove existing subscription for this service and event
    const existingIndex = subscriptions.findIndex(
      sub => sub.serviceId === serviceId
    );
    if (existingIndex !== -1) {
      subscriptions.splice(existingIndex, 1);
    }

    // Add new subscription
    const subscription: SocketEventSubscription = {
      event,
      handler,
      serviceId
    };

    subscriptions.push(subscription);

    // Add event listener to socket if connected
    if (this.socket) {
      this.socket.on(event, handler);
    }
  }

  public unsubscribe(serviceId: string, event?: string): void {
    if (event) {
      // Unsubscribe from specific event
      const subscriptions = this.eventSubscriptions.get(event);
      if (subscriptions) {
        const index = subscriptions.findIndex(
          sub => sub.serviceId === serviceId
        );
        if (index !== -1) {
          const subscription = subscriptions[index];
          // Always call off for test coverage, even if socket is disconnected
          if (this.socket) {
            this.socket.off(event, subscription.handler);
          }
          subscriptions.splice(index, 1);

          // Remove event map if no more subscriptions
          if (subscriptions.length === 0) {
            this.eventSubscriptions.delete(event);
          }
        }
      }
    } else {
      // Unsubscribe from all events for this service
      this.eventSubscriptions.forEach((subscriptions, eventName) => {
        const serviceSubs = subscriptions.filter(
          sub => sub.serviceId === serviceId
        );
        serviceSubs.forEach(sub => {
          if (this.socket) {
            this.socket.off(eventName, sub.handler);
          }
        });

        // Remove service subscriptions
        const remaining = subscriptions.filter(
          sub => sub.serviceId !== serviceId
        );
        if (remaining.length === 0) {
          this.eventSubscriptions.delete(eventName);
        } else {
          this.eventSubscriptions.set(eventName, remaining);
        }
      });
    }
  }

  public onConnectionChange(
    serviceId: string,
    callback: (connected: boolean) => void
  ): void {
    this.connectionCallbacks.set(serviceId, callback);

    // Immediately notify with current connection state, swallow errors
    try {
      callback(this.isConnected);
    } catch (error) {
      console.log(error);
      // Swallow errors to match notifyConnectionCallbacks
      if (process.env.NODE_ENV === 'test') {
        // Optionally log for test environment
        // console.error('Error in connection callback:', error);
      }
    }
  }

  public offConnectionChange(serviceId: string): void {
    this.connectionCallbacks.delete(serviceId);
  }

  private notifyConnectionCallbacks(connected: boolean): void {
    this.connectionCallbacks.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.log(error);
        // Swallow errors to ensure they never propagate
        if (process.env.NODE_ENV === 'test') {
          // Optionally log for test environment
          // console.error('Error in connection callback:', error);
        }
      }
    });
  }

  public disconnect(): void {
    if (this.socket) {
      console.log('Disconnecting socket service');
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.notifyConnectionCallbacks(false);
  }

  public getSocket(): Socket | null {
    return this.socket;
  }
}

// Export singleton instance
const socketService = SocketService.getInstance();
export default socketService;
